import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { APP_URL } from '../config';
import { homeFor } from '../lib/permissions';

export default function SetupPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const info = await authService.validateInvitationToken(token);
      setUserInfo(info);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Invalid or expired invitation link');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setValidating(true);
    try {
      const response = await authService.setupPassword(token, password);
      
      // Store token and user
      sessionStorage.setItem('signalcue_token', response.access_token);
      sessionStorage.setItem('signalcue_user', JSON.stringify(response.user));
      
      // Same rule as Login.jsx, from the same table — see web/src/lib/permissions.js.
      const redirectPath = homeFor(response.user?.role);
      
      // Redirect to app domain
      const isSameOrigin = new URL(APP_URL).origin === window.location.origin;

      if (isSameOrigin) {
        navigate(redirectPath);
      } else {
        window.location.href = `${APP_URL}${redirectPath}`;
      }
    } catch (err) {
      setError(err.message || 'Failed to set password. Please try again.');
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
            <p className="mt-4 text-gray-600">Validating invitation link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200">
          <div className="text-center">
            <div className="flex justify-center items-center mb-6">
              <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invitation Link</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <a
              href="/login"
              className="inline-block px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors"
            >
              Go to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200">
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-6">
            <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Set Up Your Password</h1>
          <p className="text-gray-600 text-sm">
            Welcome, {userInfo?.display_name}! Please set a password for your {userInfo?.role} account.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={userInfo?.email || ''}
              disabled
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE] outline-none transition"
              placeholder="Enter your password (min. 8 characters)"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-gray-500">Password must be at least 8 characters long</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE] outline-none transition"
              placeholder="Confirm your password"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={validating || !password || !confirmPassword}
            className="w-full bg-[#00C6AE] text-black py-3 rounded-lg font-semibold hover:bg-[#00C6AE]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {validating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Setting up...
              </span>
            ) : (
              'Set Password'
            )}
          </button>
        </form>

        <p className="text-center text-gray-500 text-xs mt-6">
          © 2026 SignalCue. All rights reserved.
        </p>
      </div>
    </div>
  );
}
